export function asFiniteNumber(value, fallback) {
    const n = Number(value);
    return Number.isFinite(n) ? n : fallback;
}

function normalizeConditionEntry(entry = {}, index = 0, fallbackName = "条件") {
    const baseLabel = index === 0 ? fallbackName : `条件${index + 1}`;
    const label = String(entry?.label || baseLabel).trim() || baseLabel;
    const expression = String(entry?.expression || label).trim() || label;
    return { label, expression };
}

function normalizeConditionEntries(entries = [], fallbackName = "条件", mode = "single") {
    const source = Array.isArray(entries) ? entries : [];
    const normalized = source
        .map((entry, index) => normalizeConditionEntry(entry, index, fallbackName))
        .filter((entry) => entry.label || entry.expression);
    if (!normalized.length) {
        normalized.push(normalizeConditionEntry({ label: fallbackName, expression: fallbackName }, 0, fallbackName));
    }
    return mode === "composite" ? normalized : [normalized[0]];
}

export function findDraftMotion(draft, node) {
    const axisName = String(node.axisName || "").trim();
    const motionId = String(node.motionId || node.motionDraftId || node.id || "").trim();
    const motionName = String(node.motionName || node.name || "").trim();
    for (const axis of draft.axes || []) {
        if (axisName && axis.name !== axisName) continue;
        for (const motion of axis.motions || []) {
            if ((motionId && motion.id === motionId) || (motionName && motion.name === motionName)) {
                return { axis, motion };
            }
        }
    }
    return null;
}

export function buildRuntimeAxesFromDraft(axesDraft, helpers = {}) {
    const genId = helpers.genId || ((prefix) => `${prefix}-${Math.random().toString(36).slice(2, 8)}`);
    const runtimeAxes = axesDraft.map((axisDraft, axisIndex) => {
        const axis = {
            id: genId("axis"),
            name: String(axisDraft.name || `Axis ${axisIndex + 1}`),
            motionType: axisDraft.motionType === "rotary" ? "rotary" : "linear",
            axisMaxSpeed: axisDraft.axisMaxSpeed ?? null,
            motions: []
        };
        axis.motions = (Array.isArray(axisDraft.motions) ? axisDraft.motions : []).map((motionDraft, motionIndex) => ({
            id: genId("motion"),
            draftId: String(motionDraft.id || `m${motionIndex + 1}`),
            name: String(motionDraft.name || `Motion ${motionIndex + 1}`),
            distance: asFiniteNumber(motionDraft.distance, 100),
            accel: asFiniteNumber(motionDraft.accel, 1),
            decel: asFiniteNumber(motionDraft.decel, 1),
            maxSpeed: asFiniteNumber(motionDraft.maxSpeed, 500),
            waitTime: asFiniteNumber(motionDraft.waitTime, 0),
            dependency: null,
            absoluteStartSec: 0,
            startConditionText: "",
            comment: String(motionDraft.comment || "")
        }));
        return axis;
    });

    for (const axis of runtimeAxes) {
        for (const motion of axis.motions) {
            const draftMotion = axesDraft
                .flatMap((axisDraft) => axisDraft.motions || [])
                .find((item) => String(item.id || "") === motion.draftId);
            if (draftMotion) draftMotion._runtimeMotionId = motion.id;
        }
    }

    return runtimeAxes;
}

export function prepareFlowDraftApplyData(draft, userMessage = "", helpers = {}) {
    const buildLocalFlowDraft = helpers.buildLocalFlowDraft || (() => ({ axes: [], nodes: [], connections: [] }));
    const fallbackDraft = buildLocalFlowDraft(userMessage);
    const axesDraft = Array.isArray(draft.axes) && draft.axes.length ? draft.axes : fallbackDraft.axes;
    const nodesDraft = Array.isArray(draft.nodes) && draft.nodes.length ? draft.nodes : fallbackDraft.nodes;
    const connectionsDraft = Array.isArray(draft.connections) ? draft.connections : [];
    const runtimeAxes = buildRuntimeAxesFromDraft(axesDraft, { genId: helpers.genId });
    return {
        axesDraft,
        nodesDraft,
        connectionsDraft,
        runtimeAxes,
        summary: draft.summary || "Flow draft applied."
    };
}

export function buildDrawflowNodeSpecs(nodesDraft, axesDraft, helpers = {}) {
    const genId = helpers.genId || ((prefix) => `${prefix}-${Math.random().toString(36).slice(2, 8)}`);
    const findMotion = helpers.findDraftMotion || findDraftMotion;
    return nodesDraft.map((nodeDraft) => {
        const id = String(nodeDraft.id || genId("draft-node"));
        const type = String(nodeDraft.type || "").toLowerCase();
        const x = asFiniteNumber(nodeDraft.x, 80);
        const y = asFiniteNumber(nodeDraft.y, 80);

        if (type === "start") {
            return { id, type, x, y, data: { label: nodeDraft.label || "Start" } };
        }
        if (type === "condition") {
            const conditionText = String(nodeDraft.conditionText || nodeDraft.label || "Condition").trim() || "Condition";
            const conditionMode = nodeDraft.conditionMode === "composite" ? "composite" : "single";
            return {
                id,
                type,
                x,
                y,
                data: {
                    conditionText,
                    conditionMode,
                    operator: nodeDraft.operator === "OR" ? "OR" : "AND",
                    conditions: normalizeConditionEntries(nodeDraft.conditions, conditionText, conditionMode),
                    trueLabel: nodeDraft.trueLabel || "ON",
                    falseLabel: nodeDraft.falseLabel || "OFF",
                    timeoutLabel: nodeDraft.timeoutLabel || "TIMEOUT",
                    timeoutSec: asFiniteNumber(nodeDraft.timeoutSec, 5)
                }
            };
        }
        if (type === "error") {
            return {
                id,
                type,
                x,
                y,
                data: {
                    errorCode: nodeDraft.errorCode || "E001",
                    errorMessage: nodeDraft.errorMessage || nodeDraft.label || "Error"
                }
            };
        }
        if (type === "signal_output") {
            return {
                id,
                type,
                x,
                y,
                data: {
                    label: nodeDraft.label || "Signal output",
                    signalScope: nodeDraft.signalScope === "internal_flag" ? "internal_flag" : "external",
                    signalName: nodeDraft.signalName || "Signal",
                    action: nodeDraft.action || "ON"
                }
            };
        }
        if (type === "end") {
            return {
                id,
                type,
                x,
                y,
                data: {
                    label: nodeDraft.label || "End",
                    result: nodeDraft.result || "normal"
                }
            };
        }
        if (type === "motion") {
            const foundDraft = findMotion({ axes: axesDraft }, nodeDraft);
            return {
                id,
                type,
                x,
                y,
                runtimeMotionId: foundDraft?.motion?._runtimeMotionId || ""
            };
        }
        return { id, type, x, y, data: { label: nodeDraft.label || type || "Node" } };
    });
}
